import { useEffect, useState } from "react";
import { useOutletContext, Link } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { ChartLineUp, MapTrifold, MagnifyingGlass, ArrowRight, TrendUp, Warning, ShieldCheck, BookOpenText, PaperPlaneTilt } from "@phosphor-icons/react";

export default function Dashboard() {
  const { activeBiz } = useOutletContext();
  const [ranks, setRanks] = useState([]);
  const [health, setHealth] = useState(null);
  const [loading, setLoading] = useState(true);
  const [playbookEmail, setPlaybookEmail] = useState("");
  const [sendingPlaybook, setSendingPlaybook] = useState(false);
  const [lastPlaybookSend, setLastPlaybookSend] = useState(null);

  useEffect(() => {
    if (!activeBiz) { setLoading(false); return; }
    (async () => {
      setLoading(true);
      try {
        const [r, h] = await Promise.all([
          api.get(`/businesses/${activeBiz.id}/ranks`),
          api.get(`/businesses/${activeBiz.id}/health`),
        ]);
        setRanks(r.data.ranks || []);
        setHealth(h.data);
      } catch {}
      setLoading(false);
    })();
  }, [activeBiz]);

  const sendPlaybook = async (e) => {
    e.preventDefault();
    const email = playbookEmail.trim().toLowerCase();
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email)) {
      toast.error("Enter a valid customer email address");
      return;
    }
    setSendingPlaybook(true);
    try {
      const { data } = await api.post("/playbook/send", {
        email,
        business_name: activeBiz?.name || "",
      });
      setLastPlaybookSend(data);
      toast.success(`Playbook sent to ${email}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Playbook email could not be sent");
    } finally {
      setSendingPlaybook(false);
    }
  };

  if (!activeBiz) {
    return (
      <div className="p-12 max-w-3xl mx-auto">
        <div className="card-flat text-center py-16">
          <h2 className="font-display text-3xl font-black tracking-tighter mb-3">No business yet</h2>
          <p className="text-zinc-600 mb-6">Add your first business to start tracking rankings and unlocking AI fulfillment.</p>
          <Link to="/onboarding" className="btn-primary" data-testid="dash-add-business">Add a business <ArrowRight size={14} weight="bold" /></Link>
        </div>
      </div>
    );
  }

  const top3 = ranks.filter((r) => r.in_top_3).length;
  const top10 = ranks.filter((r) => r.in_top_10).length;
  const avg = ranks.length ? (ranks.reduce((s, r) => s + r.rank, 0) / ranks.length).toFixed(1) : "—";

  return (
    <div className="p-6 lg:p-10 space-y-6 max-w-7xl">
      <header>
        <div className="label-tiny mb-1">Dashboard</div>
        <h1 className="font-display text-4xl font-black tracking-tighter" data-testid="dash-title">{activeBiz.name}</h1>
        <p className="text-zinc-500 mt-1 font-mono text-xs">{activeBiz.category} · {activeBiz.city}, {activeBiz.state}</p>
      </header>

      <section className="bento p-6 lg:p-7" data-testid="dashboard-playbook-card">
        <div className="flex flex-col lg:flex-row lg:items-center gap-6">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-2">
              <BookOpenText size={20} weight="duotone" className="text-[#002FA7]" />
              <div className="label-tiny text-[#002FA7]">Opportunity Playbook</div>
            </div>
            <h2 className="font-display text-2xl font-black tracking-tight">Send the Service Business Opportunity Playbook</h2>
            <p className="text-sm text-zinc-600 mt-2 leading-relaxed max-w-2xl">
              Enter the customer email and send their copy directly from RankTrust. This keeps Playbook delivery out of the assessment experience.
            </p>
            {lastPlaybookSend?.ok && (
              <div className="mt-3 text-xs font-mono text-emerald-700" data-testid="playbook-send-success">
                Sent to {lastPlaybookSend.email}
              </div>
            )}
          </div>

          <form onSubmit={sendPlaybook} className="w-full lg:w-[430px]" data-testid="dashboard-playbook-form">
            <label className="label-tiny mb-1.5 block" htmlFor="dashboard-playbook-email">Customer email</label>
            <div className="flex flex-col sm:flex-row gap-2">
              <input
                id="dashboard-playbook-email"
                type="email"
                autoComplete="email"
                className="input-field flex-1 min-w-0"
                placeholder="customer@business.com"
                value={playbookEmail}
                onChange={(e) => setPlaybookEmail(e.target.value)}
                data-testid="dashboard-playbook-email"
              />
              <button
                type="submit"
                className="btn-primary justify-center whitespace-nowrap"
                disabled={sendingPlaybook}
                data-testid="dashboard-playbook-send"
              >
                {sendingPlaybook ? (
                  <><span className="spinner"></span> Sending</>
                ) : (
                  <><PaperPlaneTilt size={14} weight="bold" /> Send Playbook</>
                )}
              </button>
            </div>
          </form>
        </div>
      </section>

      {health && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <ScoreHero
            label="Lead Capture Score"
            sub="Conversion engine health"
            score={health.lead_capture.score}
            trend={health.lead_capture.trend_pct}
            components={health.lead_capture.components}
            color="#002FA7"
            testid="dash-lead-score"
            Icon={TrendUp}
          />
          <ScoreHero
            label="Quality Health"
            sub="Anti-spam · ToS compliance · AI content quality"
            score={health.quality.score}
            warnings={health.quality.warnings}
            components={health.quality.components}
            color="#10A37F"
            testid="dash-quality-score"
            Icon={ShieldCheck}
          />
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-12 gap-4">
        <Stat className="md:col-span-3" label="Total Keywords" value={ranks.length} testid="stat-total" />
        <Stat className="md:col-span-3" label="In Top 3" value={top3} accent="text-[#22C55E]" testid="stat-top3" />
        <Stat className="md:col-span-3" label="In Top 10" value={top10} accent="text-[#EAB308]" testid="stat-top10" />
        <Stat className="md:col-span-3" label="Avg Rank" value={avg} testid="stat-avg" />

        <div className="md:col-span-8 bento p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-display text-xl font-bold">Keyword Performance</h3>
            <Link to="/app/rank-tracker" className="btn-ghost text-xs" data-testid="dash-see-all-ranks">See all <ArrowRight size={12} weight="bold" /></Link>
          </div>
          {loading ? (
            <div className="py-12 flex justify-center"><span className="spinner text-zinc-400"></span></div>
          ) : ranks.length === 0 ? (
            <div className="py-8 text-zinc-500 text-sm">No keywords tracked yet. Add some in <Link to="/app/settings" className="text-[#002FA7] font-semibold">Settings</Link>.</div>
          ) : (
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left">
                  <th className="label-tiny pb-3">Keyword</th>
                  <th className="label-tiny pb-3">Search Vol</th>
                  <th className="label-tiny pb-3">Rank</th>
                </tr>
              </thead>
              <tbody>
                {ranks.slice(0, 6).map((r) => (
                  <tr key={r.keyword} className="border-t border-zinc-100">
                    <td className="py-3 font-medium">{r.keyword}</td>
                    <td className="py-3 font-mono text-xs text-zinc-600">{r.search_volume.toLocaleString()}/mo</td>
                    <td className="py-3"><RankPill rank={r.rank} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        <div className="md:col-span-4 grid gap-4">
          <ActionCard testid="dash-action-audit" to="/app/audit" icon={MagnifyingGlass} title="Run AI Audit" desc="Get a Top-3 readiness score and prioritized fixes." />
          <ActionCard testid="dash-action-grid" to="/app/geo-grid" icon={MapTrifold} title="Geo-Grid Tracker" desc="See how you rank across 25 lat/long points." />
          <ActionCard testid="dash-action-content" to="/app/content" icon={TrendUp} title="Generate AI Post" desc="SEO-rich GBP post drafted for you." />
        </div>
      </div>
    </div>
  );
}

export function Stat({ label, value, accent = "", className = "", testid }) {
  return (
    <div className={`bento p-5 ${className}`} data-testid={testid}>
      <div className="label-tiny">{label}</div>
      <div className={`mt-2 font-display text-4xl font-black tabular-nums ${accent}`}>{value}</div>
    </div>
  );
}

export function RankPill({ rank }) {
  if (!rank) return <span className="text-zinc-400">—</span>;
  const cls = rank <= 3 ? "top3" : rank <= 10 ? "top10" : "poor";
  return <span className={`rank-pill ${cls}`} data-testid={`rank-pill-${rank}`}>#{rank}</span>;
}

function ActionCard({ to, icon: Icon, title, desc, testid }) {
  return (
    <Link to={to} className="bento p-5 hover:border-[#002FA7] block group" data-testid={testid}>
      <Icon size={20} className="text-[#002FA7] mb-3" />
      <div className="font-display font-bold text-base">{title}</div>
      <div className="text-zinc-600 text-xs mt-1 leading-relaxed">{desc}</div>
      <div className="mt-3 label-tiny text-[#002FA7] flex items-center gap-1 group-hover:gap-2 transition-all">Run <ArrowRight size={10} weight="bold" /></div>
    </Link>
  );
}

function ScoreHero({ label, sub, score, trend, warnings, components, color, testid, Icon }) {
  const grade = score >= 80 ? "Excellent" : score >= 60 ? "Healthy" : score >= 40 ? "Needs work" : "At risk";
  const gradeColor = score >= 80 ? "text-[#22C55E]" : score >= 60 ? "text-[#10A37F]" : score >= 40 ? "text-[#EAB308]" : "text-[#EF4444]";
  return (
    <div className="bento p-6 relative overflow-hidden" data-testid={testid}>
      <div className="flex items-start gap-4">
        <div className="flex-1 min-w-0">
          <div className="label-tiny flex items-center gap-2"><Icon size={12} weight="bold" /> {label}</div>
          <div className="flex items-baseline gap-3 mt-2">
            <span className="font-display text-7xl font-black tabular-nums" style={{ color }}>{score}</span>
            <span className="text-zinc-400 font-display text-3xl">/100</span>
            {typeof trend === "number" && (
              <span className={`font-mono text-xs font-bold ${trend >= 0 ? "text-[#22C55E]" : "text-[#EF4444]"}`}>
                {trend >= 0 ? "▲" : "▼"} {Math.abs(trend)}% wow
              </span>
            )}
          </div>
          <div className={`font-display text-lg font-bold mt-1 ${gradeColor}`}>{grade}</div>
          <div className="text-xs text-zinc-500 mt-1 font-mono">{sub}</div>
        </div>
      </div>
      <div className="mt-5 space-y-2">
        {Object.entries(components || {}).slice(0, 4).map(([k, v]) => (
          <div key={k} className="flex items-center gap-3 text-xs">
            <span className="text-zinc-600 font-mono w-44 capitalize truncate">{k.replace(/_/g, " ")}</span>
            <div className="flex-1 h-1.5 bg-zinc-100">
              <div className="h-full" style={{ width: `${v}%`, background: color }}></div>
            </div>
            <span className="font-mono text-zinc-700 tabular-nums w-8 text-right">{v}</span>
          </div>
        ))}
      </div>
      {warnings && warnings.length > 0 && (
        <div className="mt-4 pt-4 border-t border-zinc-100 space-y-2">
          {warnings.slice(0, 2).map((w, i) => (
            <div key={i} className="flex items-start gap-2 text-xs" data-testid={`warning-${w.code}`}>
              <Warning size={12} weight="fill" className={`shrink-0 mt-0.5 ${w.severity === "high" ? "text-[#EF4444]" : w.severity === "medium" ? "text-[#EAB308]" : "text-zinc-500"}`} />
              <span className="text-zinc-700">{w.msg}</span>
            </div>
          ))}
          {warnings.length > 2 && (
            <div className="text-xs text-zinc-500 font-mono">+{warnings.length - 2} more issue{warnings.length - 2 === 1 ? "" : "s"}</div>
          )}
        </div>
      )}
    </div>
  );
}
